import React, { useState, FormEvent, useEffect } from 'react';
import './Login.scss';
import './PasswordSet.scss';
import { BaseReact } from '../../base.model';
import { Link } from 'react-router-dom';
import { ApolloError, useMutation } from '@apollo/client';
import { graphqlSchema } from '../../services/graphql.schema';
import { LogoBanner } from '../visuals/banner.logo';
import InputAnimated from './input/input';

// Mirrors the passwordReset resolver's minimum length
const MIN_PASSWORD_LENGTH = 6;

interface PasswordSetProps extends BaseReact { }

function PasswordSet({ history, match }: PasswordSetProps) {
    const [isValidToken, setIsValidToken] = useState(false);
    const [isTestingToken, setIsTestingToken] = useState(true);
    const [resetDone, setResetDone] = useState(false);
    const [saving, setSaving] = useState(false);
    // The link expired or was already used while the form was open, saving can no longer succeed
    const [tokenExpired, setTokenExpired] = useState(false);
    const [resetError, setResetError] = useState('');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const token = (match.params as { token: string }).token;
    const passwordsMismatch = !!password && !!confirmPassword && password !== confirmPassword;
    const passwordTooShort = !!password && password.length < MIN_PASSWORD_LENGTH;

    const [validateToken] = useMutation(graphqlSchema.PROFILE.validateToken, {
        onCompleted: ({ validateToken }: { validateToken: boolean }) => {
            setIsTestingToken(false)
            setIsValidToken(validateToken)
        },
        onError: () => {
            setIsTestingToken(false);
            setIsValidToken(false)
        }
    })

    useEffect(() => {
        validateToken({
            variables: {
                token
            }
        })
    }, [token, validateToken])

    const [applyNewPassword] = useMutation(graphqlSchema.PROFILE.passwordReset, {
        onCompleted: () => {
            // Sessions are cookie-only and created at login, send the user to log in with the new password
            setResetDone(true);
            setTimeout(() => history.push('/login'), 3000);
        }, onError: (e: ApolloError) => {
            console.log('Error requesting password reset: ', e);
            setSaving(false);
            // The password length is checked before submitting, so a BAD_USER_INPUT here means the token
            // expired or was already used. Anything else (network, server) is worth retrying.
            if (e.graphQLErrors?.[0]?.extensions?.code === 'BAD_USER_INPUT') {
                setTokenExpired(true);
                return;
            }
            setResetError('No fue posible cambiar la contraseña, intenta de nuevo');
        }
    })

    const submitRecover = (e: FormEvent) => {
        e.preventDefault();
        if (!password || passwordsMismatch || passwordTooShort || tokenExpired) {
            return;
        }
        setSaving(true);
        setResetError('');
        applyNewPassword({
            variables: {
                token,
                newPassword: password
            }
        })
    }

    const renderContent = () => {
        if (resetDone) {
            return <p className="_status success">Contraseña actualizada, redirigiendo al inicio de sesión...</p>;
        }
        if (isTestingToken) {
            return <p className="_status">Confirmando tu enlace, espera...</p>;
        }
        if (!isValidToken) {
            return <p className="_status error">El enlace para cambiar la contraseña es inválido o ya venció.</p>;
        }

        const errorText =
            tokenExpired ? 'No fue posible cambiar la contraseña, solicita un nuevo enlace'
                : passwordsMismatch ? 'Las contraseñas no coinciden'
                    : passwordTooShort ? `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres`
                        : resetError;
        return (
            <form onSubmit={submitRecover}>
                <InputAnimated
                    hasError={passwordTooShort || passwordsMismatch}
                    type="password" label="Nueva contraseña"
                    value={password} onChange={setPassword} />
                <InputAnimated
                    hasError={passwordsMismatch}
                    type="password" label="Confirmar contraseña"
                    value={confirmPassword} onChange={setConfirmPassword} />
                {!!errorText && <p className="_formError">{errorText}</p>}
                <div className="_actions">
                    <span></span>
                    <button type="submit" disabled={saving || tokenExpired || passwordsMismatch || passwordTooShort || !password || !confirmPassword}>
                        {saving ? 'Guardando' : 'Guardar'}
                    </button>
                </div>
            </form>
        );
    }

    // Only a dead link gets a way out, to request a new one
    const linkIsDead = (!isTestingToken && !isValidToken && !resetDone) || tokenExpired;

    return (
        <div id="PasswordSet">
            <div className="_loginContainer">
                <LogoBanner />

                <div className="_logoSelect">
                    <h2 className="gray">Cambiar contraseña</h2>
                    {renderContent()}
                </div>
                {
                    linkIsDead &&
                    <Link to="/login?recover=true">¿Necesitas otro enlace? <b>Recupera tu contraseña</b></Link>
                }
            </div>
        </div>
    )
}

export default PasswordSet;
